/**
 * 「本月三件事」卡的**纯逻辑层**（批 `155` · E 域 E2 · 照 `features/cashflow.ts` 的范式）
 *
 * ## 为什么单独一层
 *
 * ① 断言无需渲染（静态渲染基座零依赖直测）；
 * ② 「哪一条该说什么话」集中在一处 —— 散在 JSX 里就会出现"预算已超却配了『接近上限』"这种
 *    只有肉眼能发现的错。
 *
 * ## 🔴 本文件**一个数都不算**
 *
 * 全部字段来自服务端 `MonthlyInsightsView`：金额、百分比、条数、增量、σ 倍数**都是下发的**。
 * 这里只做三件事：**选词 · 排版 · 拼句式**。
 *
 * ⇒ 因此没有 `/`、没有 `reduce`、没有 `Math.*`；金额一律走 `formatMoneyText()`
 * （`DR-036`：符号在币种之后，减号是 U+2212），百分比走预算页那支 `percentText()`
 * （`report-labels.ts` 同款复用，避免第二套百分比格式）。
 *
 * ## 🔴 文案红线（`AC-E2.8` + 规格 §15）
 *
 * 每条必须**三段齐全**：**结论（含数字）+ 证据（哪来的数）+ 一个动作**。
 * 缺任何一段都不是"本月三件事"，而是一句正确的废话。
 * 🔴 **不劝人**：不出现"你应该""建议控制""注意别乱花" —— 系统只把数字与动作摆出来，
 * 怎么处置是用户的事（同 `cashflow.ts` 的文案红线）。
 */

import {
  InsightKind,
  InsightSeverity,
  e4ToTrimmedText,
  formatMoneyText,
  type Cents,
  type InsightDataView,
  type InsightItemView,
  type MonthlyInsightsView,
} from '@jiaying/shared';
import { percentText } from './budget-labels.js';

/** 卡片标题（唯一名 —— 不出现第二个叫法） */
export const INSIGHTS_TITLE = '本月三件事';
/** 副标题（`AC-E2.11` 固定文案：⚠️ "本地规则生成，未上传流水" 一个字都不能改） */
export const INSIGHTS_SUBTITLE = '本地规则生成，未上传流水';
export const INSIGHTS_REFRESH_LABEL = '刷新';
export const INSIGHTS_KEY_BADGE = '先看这条';

/** 正向空态（`AC-E2.7`）—— 🔴 不写"暂无数据"（那会让人怀疑自己没记账） */
export const INSIGHTS_EMPTY_TITLE = '本月没有需要你处理的事';
export const INSIGHTS_EMPTY_HINT = '预算都在轨道内、没有异常支出、目标按节奏走。空着不是没算，是真的没有。';
export const INSIGHTS_ERROR_PREFIX = '本月三件事读取失败：';

/**
 * 动作目标（前端路由）。
 *
 * 🔴 只在**这一处**出现路由字面量：七个 kind 各自指哪儿是产品决定，
 * 散在 JSX 里就会出现"第六条还指旧路径"的死链（改了路由没人知道要改这里）。
 */
export const INSIGHT_ACTION_TARGETS = {
  spendable: '/plan',
  budget: '/plan',
  anomaly: '/journal',
  categoryDrift: '/report',
  goal: '/goals',
  recurring: '/plan',
  pending: '/reminders',
} as const;

/** 一条可渲染的建议（结论 + 证据 + 动作，三段齐全） */
export interface InsightRow {
  /** 稳定 key（服务端不下发 id —— 顺序即身份，刷新后会变；React 只需要列表内唯一） */
  readonly key: string;
  readonly kind: InsightKind;
  /** 结论（含数字） */
  readonly title: string;
  /** 证据行（这个数字是哪来的） */
  readonly evidence: string;
  readonly actionLabel: string;
  readonly actionTarget: string;
  /** 第 1 条视觉加重（`AC-E2.10`）—— 由**服务端**判，端侧不重排 */
  readonly isKey: boolean;
  /** 朱红（铁律 8：只有"最高"档是预警） */
  readonly alarming: boolean;
}

/** 详见 {@link INSIGHT_ACTION_TARGETS} 的分派 —— 与 `data.kind` 一一对应 */
function targetOf(kind: InsightKind): string {
  switch (kind) {
    case InsightKind.SpendableNegative:
      return INSIGHT_ACTION_TARGETS.spendable;
    case InsightKind.BudgetDeviation:
      return INSIGHT_ACTION_TARGETS.budget;
    case InsightKind.Anomaly:
      return INSIGHT_ACTION_TARGETS.anomaly;
    case InsightKind.CategoryDrift:
      return INSIGHT_ACTION_TARGETS.categoryDrift;
    case InsightKind.GoalBehind:
      return INSIGHT_ACTION_TARGETS.goal;
    case InsightKind.RecurringHint:
      return INSIGHT_ACTION_TARGETS.recurring;
    case InsightKind.PendingPayable:
      return INSIGHT_ACTION_TARGETS.pending;
    default:
      return assertNever(kind);
  }
}

/**
 * 穷举哨兵 —— 新增第 8 种 `kind` 时**这里编译失败**。
 *
 * 🔴 这比"默认返回空串"强得多：漏配一条的后果是用户点不动那条建议，
 * 而那是**不会报错**的坏体验。
 */
function assertNever(x: never): never {
  throw new Error(`未处理的建议类型：${String(x)}`);
}

/** 单条的文案（`data` 的判别式在这里被穷举） */
function textOf(
  data: InsightDataView,
  currency: string,
): { title: string; evidence: string; actionLabel: string } {
  const money = (cents: Cents): string => formatMoneyText(cents, currency);
  switch (data.kind) {
    case InsightKind.SpendableNegative:
      return {
        title: `本月可花已为负，透支 ${money(data.overspentBy)}`,
        evidence: '可花 = 预算额度 − 已花（含待缴未付）',
        actionLabel: '看构成',
      };
    case InsightKind.BudgetDeviation:
      return data.level === 'over'
        ? {
            // ⚠️ 不写「${名}已超出预算」：总预算卡的 `categoryName` 就是「总预算」，
            //    会渲染成「总预算已超出预算」这种重复句（真浏览器取证时一眼看到）
            title: `${data.categoryName}超支 ${money(data.overspentCents)}`,
            evidence: `预算 ${money(data.amount)} · 已用 ${money(data.spent)}（含待缴未付）`,
            actionLabel: '调预算',
          }
        : {
            title: `${data.categoryName}已用 ${percentText(data.usedE4)}，接近上限`,
            evidence: `预算 ${money(data.amount)} · 已用 ${money(data.spent)}（含待缴未付）`,
            actionLabel: '看执行',
          };
    case InsightKind.Anomaly:
      return {
        title: `发现 ${data.count} 笔异常大额，合计 ${money(data.totalCents)}`,
        evidence:
          data.topSigmaE4 === null
            ? '按本月同类支出的均值与波动判定（σ 离群）'
            : `单笔最高约为日常均值的 ${e4ToTrimmedText(data.topSigmaE4)} 倍（σ 离群判定）`,
        actionLabel: '看这几笔',
      };
    case InsightKind.CategoryDrift:
      return {
        title: `${data.categoryName}比上一期多花 ${money(data.deltaCents)}（+${percentText(data.momE4)}）`,
        evidence: `上一期 ${money(data.previousCents)} → 本期 ${money(data.currentCents)}`,
        actionLabel: '看分类',
      };
    case InsightKind.GoalBehind:
      return {
        title:
          data.pace === 'overdue'
            ? `「${data.goalName}」已过截止日，还差 ${money(data.remainingCents)}`
            : `「${data.goalName}」按当前节奏赶不上截止日，还差 ${money(data.remainingCents)}`,
        evidence:
          data.daysLeft === null
            ? '这个目标没有截止日，看不出节奏'
            : `距截止日还有 ${data.daysLeft} 天`,
        actionLabel: '去攒一笔',
      };
    case InsightKind.RecurringHint:
      return {
        // ⚠️ 不写「发现 3 笔疑似周期支出」：`occurrences` 是**消费日数**（同日多笔已合并），
        //    说成"3 笔"会与流水页的真实笔数对不上（真浏览器取证时发现的措辞坑）
        title: `「${data.label}」近 ${data.occurrences} 次金额相近，可能是固定支出`,
        evidence: `每次约 ${money(data.typicalAmountCents)} · 下次预计 ${data.nextExpectedDay}`,
        actionLabel: '设成周期记账',
      };
    case InsightKind.PendingPayable:
      return {
        title: `有 ${money(data.unpaidPending)} 待缴未付`,
        evidence: '这些钱还没从账户里出去，但已经占用了本月预算',
        actionLabel: '去处理',
      };
    default:
      return assertNever(data);
  }
}

/**
 * 响应 → 可渲染行。
 *
 * 🔴 **不重排、不裁剪**：顺序与条数完全由服务端 `rankInsights()` 决定（`AC-E2.5`/`E2.6`）。
 * 端侧再排一次就会出现"服务端说第 1 条是预算、界面上第 1 条是异常"的分叉。
 */
export function toInsightRows(view: MonthlyInsightsView): InsightRow[] {
  return view.items.map((item: InsightItemView, index: number) => {
    const t = textOf(item.data, view.baseCurrency);
    return {
      // ⚠️ key 只用"位置 + kind"，**不含金额**：金额一变 key 就变，React 会整块重建而非更新
      key: `${item.data.kind}-${index}`,
      kind: item.data.kind,
      title: t.title,
      evidence: t.evidence,
      actionLabel: t.actionLabel,
      actionTarget: targetOf(item.data.kind),
      isKey: item.isKey,
      alarming: item.severity === InsightSeverity.Highest,
    };
  });
}
